import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFriends } from '../friends/friendsContext';
import { interruptForPvpMatch } from '../matchmaking/interruptForPvpMatch';
import { showPvpMatchInterruptNotice } from '../matchmaking/pvpMatchInterruptTiming';
import {
  shouldHandlePvpMatchFound,
  subscribeRematchClientMessages,
} from './rematchWsBridge';
import RematchRequestDialog from './RematchRequestDialog';

const RematchContext = createContext(null);

export function useRematch() {
  const ctx = useContext(RematchContext);
  if (!ctx) {
    throw new Error('useRematch must be used within RematchProvider');
  }
  return ctx;
}

export function useRematchOptional() {
  return useContext(RematchContext);
}

export function RematchProvider({ children, isLoggedIn }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const locationPathRef = useRef(location.pathname);
  locationPathRef.current = location.pathname;
  const tRef = useRef(t);
  tRef.current = t;
  const { sendWsPayload } = useFriends();

  const [outgoingWait, setOutgoingWait] = useState(null);
  const [incomingOffer, setIncomingOffer] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!outgoingWait?.requestedAt) {
      setElapsedSeconds(0);
      return undefined;
    }
    const tick = () => {
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - outgoingWait.requestedAt) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [outgoingWait]);

  const clearRematchUi = useCallback(() => {
    setOutgoingWait(null);
    setIncomingOffer(null);
  }, []);

  const handleMatchFound = useCallback(async (data) => {
    clearRematchUi();
    if (!shouldHandlePvpMatchFound(data.gameId)) {
      return;
    }
    const matchPayload = {
      gameId: data.gameId,
      gameState: data.gameState,
      yourTurn: data.yourTurn,
    };
    try {
      try {
        await showPvpMatchInterruptNotice(tRef.current('matchmaking.pvpMatchFoundSwitching'));
      } catch (noticeErr) {
        console.error('Rematch interrupt notice failed:', noticeErr);
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('coda-pvp-interrupt', { detail: { gameId: matchPayload.gameId } })
        );
      }
      await interruptForPvpMatch(navigateRef.current, locationPathRef.current, matchPayload);
    } catch (err) {
      console.error('Rematch interrupt failed:', err);
    }
  }, [clearRematchUi]);

  useEffect(() => {
    return subscribeRematchClientMessages((data) => {
      if (data.type === 'rematch-waiting') {
        setIncomingOffer(null);
        setOutgoingWait({
          gameId: data.gameId,
          kind: data.kind,
          opponentUserId: data.opponentUserId,
          opponentUsername: data.opponentUsername,
          requestedAt: typeof data.requestedAt === 'number' ? data.requestedAt : Date.now(),
        });
        return;
      }
      if (data.type === 'rematch-offered') {
        setOutgoingWait(null);
        setIncomingOffer({
          gameId: data.gameId,
          kind: data.kind,
          fromUserId: data.fromUserId,
          fromUsername: data.fromUsername,
        });
        return;
      }
      if (data.type === 'rematch-cancelled') {
        clearRematchUi();
        return;
      }
      if (data.type === 'match-found') {
        handleMatchFound(data);
      }
    });
  }, [clearRematchUi, handleMatchFound]);

  const requestRematch = useCallback((gameId, kind) => {
    if (!isLoggedIn) {
      throw new Error('requestRematch: not logged in');
    }
    sendWsPayload({ type: 'rematch-request', gameId, kind });
  }, [isLoggedIn, sendWsPayload]);

  const cancelRematch = useCallback(() => {
    sendWsPayload({ type: 'rematch-cancel' });
  }, [sendWsPayload]);

  const acceptRematch = useCallback(() => {
    sendWsPayload({ type: 'rematch-accept' });
  }, [sendWsPayload]);

  const rejectRematch = useCallback(() => {
    sendWsPayload({ type: 'rematch-reject' });
  }, [sendWsPayload]);

  const syncRematchFromServer = useCallback((rematchWait) => {
    if (!rematchWait) {
      return;
    }
    if (rematchWait.inRematchWait === true) {
      setIncomingOffer(null);
      setOutgoingWait({
        gameId: rematchWait.gameId,
        kind: rematchWait.kind,
        opponentUserId: rematchWait.opponentUserId,
        opponentUsername: rematchWait.opponentUsername,
        requestedAt: rematchWait.queuedAt,
      });
      return;
    }
    if (rematchWait.incomingOffer) {
      setOutgoingWait(null);
      setIncomingOffer(rematchWait.incomingOffer);
    }
  }, []);

  const value = useMemo(
    () => ({
      outgoingWait,
      incomingOffer,
      elapsedSeconds,
      inRematchWait: outgoingWait != null,
      requestRematch,
      cancelRematch,
      acceptRematch,
      rejectRematch,
      syncRematchFromServer,
    }),
    [
      outgoingWait,
      incomingOffer,
      elapsedSeconds,
      requestRematch,
      cancelRematch,
      acceptRematch,
      rejectRematch,
      syncRematchFromServer,
    ]
  );

  return (
    <RematchContext.Provider value={value}>
      {children}
      <RematchRequestDialog
        offer={incomingOffer}
        onAccept={acceptRematch}
        onReject={rejectRematch}
      />
    </RematchContext.Provider>
  );
}
